/**
 * Storage Service - Abstract interface for data persistence
 * Uses database API with localStorage fallback
 */

const STORAGE_KEY = 'health_assessments';
const USE_API = true; // Set to true to use database API

class StorageService {
  /**
   * Get all assessments
   */
  static async getAllAssessments() {
    if (USE_API) {
      try {
        const response = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'assessment.list' })
        });
        
        if (response.ok) {
          const { assessments } = await response.json();
          // Convert array to object keyed by id for compatibility
          const assessmentsObj = {};
          assessments.forEach(a => {
            assessmentsObj[a.id] = a;
          });
          return assessmentsObj;
        }
      } catch (error) {
        console.error('API error, falling back to localStorage:', error);
      }
    }
    
    // Fallback to localStorage
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error getting assessments:', error);
      return {};
    }
  }

  /**
   * Get single assessment by ID
   */
  static async getAssessment(id) {
    if (USE_API) {
      try {
        const response = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            action: 'assessment.get',
            data: { id }
          })
        });
        
        if (response.ok) {
          const { assessment } = await response.json();
          if (assessment) {
            return assessment;
          }
        } else {
          console.log('API returned non-ok status:', response.status, 'falling back to localStorage');
        }
      } catch (error) {
        console.log('API error, falling back to localStorage:', error.message);
      }
    }
    
    // Fallback to localStorage
    console.log('Fetching assessment from localStorage, id:', id);
    try {
      const assessments = await this.getAllAssessments();
      const assessment = assessments[id];
      console.log('Found in localStorage:', !!assessment);
      return assessment || null;
    } catch (error) {
      console.error('Error getting assessment from localStorage:', error);
      return null;
    }
  }

  /**
   * Save new assessment
   */
  static async saveAssessment(assessment) {
    if (USE_API) {
      try {
        // Get user email from auth
        const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
        
        const response = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            action: 'assessment.create',
            data: {
              email: authUser.email || assessment.personalInfo?.email,
              personalInfo: assessment.personalInfo,
              status: assessment.status || 'in_progress'
            }
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log('Assessment saved to database:', result);
          return { success: true, data: result.assessment };
        }
      } catch (error) {
        console.error('API error, falling back to localStorage:', error);
      }
    }
    
    // Fallback to localStorage
    try {
      const assessments = await this.getAllAssessments();
      assessments[assessment.id] = assessment;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(assessments));
      return { success: true, data: assessment };
    } catch (error) {
      console.error('Error saving assessment:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update existing assessment
   */
  static async updateAssessment(id, updates) {
    if (USE_API) {
      try {
        console.log('🔄 Updating assessment:', id, updates);
        const response = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            action: 'assessment.update',
            data: { id, updates }
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log('✅ Assessment updated in database:', result);
          return { success: true, data: result.assessment };
        } else {
          const errorText = await response.text();
          console.error('❌ API update failed:', response.status, errorText);
        }
      } catch (error) {
        console.error('❌ API error, falling back to localStorage:', error);
      }
    }
    
    // Fallback to localStorage
    try {
      const assessments = await this.getAllAssessments();
      if (!assessments[id]) {
        throw new Error('Assessment not found');
      }
      
      assessments[id] = {
        ...assessments[id],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(assessments));
      return { success: true, data: assessments[id] };
    } catch (error) {
      console.error('Error updating assessment:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete assessment
   */
  static async deleteAssessment(id) {
    try {
      const assessments = await this.getAllAssessments();
      delete assessments[id];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(assessments));
      return { success: true };
    } catch (error) {
      console.error('Error deleting assessment:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get assessments by status
   */
  static async getAssessmentsByStatus(status) {
    try {
      const assessments = await this.getAllAssessments();
      return Object.values(assessments).filter(a => a.status === status);
    } catch (error) {
      console.error('Error filtering assessments:', error);
      return [];
    }
  }

  /**
   * Export all data (for backup or migration)
   */
  static async exportData() {
    try {
      const assessments = await this.getAllAssessments();
      const dataStr = JSON.stringify(assessments, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `health-assessments-${new Date().toISOString()}.json`;
      link.click();
      
      URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      console.error('Error exporting data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Import data (for restore or migration)
   */
  static async importData(jsonData) {
    try {
      const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return { success: true };
    } catch (error) {
      console.error('Error importing data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Clear all data (use with caution!)
   */
  static async clearAll() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      return { success: true };
    } catch (error) {
      console.error('Error clearing data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get storage statistics
   */
  static async getStats() {
    try {
      const assessments = await this.getAllAssessments();
      const assessmentArray = Object.values(assessments);
      
      return {
        total: assessmentArray.length,
        inProgress: assessmentArray.filter(a => a.status === 'in_progress').length,
        completed: assessmentArray.filter(a => a.status === 'completed').length,
        storageSize: new Blob([JSON.stringify(assessments)]).size
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      return { total: 0, inProgress: 0, completed: 0, storageSize: 0 };
    }
  }
}

export default StorageService;
